'use client';

import { INavListItem } from '@/api/types/public';
import MagicJump from '@/components/MagicJump';
import { useReporting } from '@/hooks/useReporting';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { ProviderType } from '@/reporting';
import { setSession } from '@/utils/tools';
import { Collapse } from '@mui/material';
import { useEffect, useState } from 'react';
import LensProcessNav from './LensProcessNav';
import TextNav from './TextNav';

interface MenuItemTabletProps {
  item: INavListItem;
  tabIndex: number;
  onCloseNav: () => void;
}

// 平板导航项组件 - 对应Vue中的OneLevelMin
const MenuItemTablet = ({ item, tabIndex, onCloseNav }: MenuItemTabletProps) => {
  const [openCollapse, setOpenCollapse] = useState(false);
  const { trackEvent } = useReporting();
  const { cnName } = useRouteMetadata();

  // 计算属性 - 对应Vue的computed
  const hasChildren = !!item.children?.length;

  // 初始化时检查是否有图片导航项，如果有则默认展开 (类似Vue的watch逻辑)
  useEffect(() => {
    if (item.children) {
      const hasImageNav = item.children.some((cItem) => cItem?.template_id === 3);
      if (hasImageNav) {
        setOpenCollapse(true);
      }
    }
  }, [item.children]);

  // 处理展开/收起导航 - 对应Vue的openNav方法
  const handleOpenNav = () => {
    if (!hasChildren) return;

    setOpenCollapse(!openCollapse);
  };

  // 处理主导航跳转前的埋点 - 对应Vue模板中的多个埋点调用
  const handleMainNavBeforeJump = () => {
    if (!item.content?.nav_name) return;
    setSession('zl_goodsListEnterPageScene', '全局导航');

    // SD_TopNavTab_Click
    trackEvent(
      {
        eventName: 'TopNavTab_Click',
        properties: {
          tab_name: item.content.nav_name,
          tab_index: tabIndex,
          url_path: window.location.href,
          page_name: cnName, // 使用路由元数据中的中文名称
        },
      },
      [ProviderType.SENSORS],
    );

    // 关闭导航
    onCloseNav();

    trackEvent(
      {
        eventName: 'TopNavigationTab_Click',
        properties: {
          tab_name: item.content.nav_name,
          tab_index: tabIndex,
          url_path: window.location.href,
          page_name: cnName, // 使用路由元数据中的中文名称
        },
      },
      [ProviderType.SENSORS],
    );
  };

  if (!item.content) return null;

  return (
    <div className="border-b border-[#eee]">
      {/* 主导航内容区域 */}
      <div
        className={`font-bb flex justify-between items-center bg-white py-[20px] ${openCollapse ? 'open' : ''}`}
      >
        {/* 一级导航链接 */}
        <MagicJump
          className="one-level"
          item={{
            jump_type: item.content.jump_type || 0,
            Jump_target: item.content.Jump_target || '',
          }}
          onBeforeJump={handleMainNavBeforeJump}
        >
          <span className="border-b border-transparent hover:border-primary-black text-[16px]">
            {item.content.nav_name}
          </span>
        </MagicJump>

        {/* 箭头展开/收起按钮 */}
        <span
          className="arrow-box w-[30px] h-[30px] cursor-pointer flex justify-end items-center"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenNav();
          }}
        >
          {hasChildren && (
            <i
              className={`arrow block relative w-[10px] h-[10px] cursor-pointer ${openCollapse ? 'show' : ''}`}
            >
              <span className="block absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#282828] w-[10px] h-[2px]" />
              <span
                className="block absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#282828] w-[2px] transition-all duration-[300ms]"
                style={{
                  height: openCollapse ? '0px' : '10px',
                }}
              />
            </i>
          )}
        </span>
      </div>

      {/* 折叠展开内容 */}
      {hasChildren && (
        <Collapse in={openCollapse} timeout="auto" unmountOnExit>
          <div>
            {item.children?.map((cItem, eindex) => {
              // TextNav组件 - template_id === 2
              if (cItem.template_id === 2) {
                return (
                  <TextNav
                    key={cItem.id}
                    item={cItem}
                    firstLevelIndex={eindex}
                    firstLevelTitle={item.content?.nav_name || ''}
                    secondLevelIndex={0}
                    isLast={eindex === (item.children?.length || 0) - 1}
                  />
                );
              }

              // LensProcessNav组件 - template_id === 4
              if (cItem.template_id === 4) {
                return <LensProcessNav key={cItem.id} />;
              }

              return null;
            })}
          </div>
        </Collapse>
      )}
    </div>
  );
};

export default MenuItemTablet;
